import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import React from 'react';

import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import VideoButton from '@pflegia/ui/shared/video-button';

import Headline from '../headline/Headline';

interface OfferedValue {
  key: string;
  videoId: string;
  alt?: string;
  altIconText?: string;
}

export interface OfferedValuesProps {
  values: OfferedValue[];
}

const OfferedValues: React.FC<OfferedValuesProps> = ({ values }) => {
  const { t } = useTranslation('landingPage', { keyPrefix: 'offeredValues' });
  const [selectedValue, selectValue] = React.useState(values[0]);

  const { sourceDomainName } = useFlavorFromRoute();

  return (
    <div>
      <Headline i18nKey={`landingPage:offeredValues.title`} />
      <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
        {values.map(value => {
          const selected = value.key === selectedValue.key;
          return (
            <button
              className={clsx(
                'flex flex-col items-center justify-center',
                'overflow-hidden rounded-2xl border border-gray-200',
                'px-6 py-2.5 text-lg',
                'outline-hidden',
                'transition-colors duration-150 ease-in-out',
                'hover:border-main-blue hover:bg-main-blue hover:text-white',
                'focus:border-main-blue focus:bg-main-blue focus:text-white',
                'md:py-6 md:text-2xl md:shadow-xl',
                selected ? 'border-main-blue bg-main-blue text-white shadow-xl' : 'border-primary-200 text-default bg-white',
              )}
              key={value.key}
              onClick={() => {
                selectValue(value);
              }}>
              <Image
                alt={selectedValue.altIconText ?? ''}
                height={90}
                src={`/assets/offered-values/${value.key}${selected ? '-active' : ''}.svg`}
                width={90}
              />
              {t(`${value.key}.title`)}
            </button>
          );
        })}
      </div>
      <div className='mt-8 flex items-center gap-4 md:mx-16 md:mt-14'>
        <div className='hidden md:block'>
          <VideoButton
            alt={selectedValue.alt ?? ''}
            circle
            imgSrc={`/assets/video-thumbnails/${selectedValue.videoId}.jpg`}
            videoId={selectedValue.videoId}
          />
        </div>
        <div>
          <h2 className='text-main-blue mb-2 text-2xl'>{t(`${selectedValue.key}.title`)}</h2>
          <p className='text-default'>{t(`${selectedValue.key}.description`, { domain: sourceDomainName })}</p>
        </div>
      </div>
    </div>
  );
};

export default OfferedValues;
